import React, { useState } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, Download, Upload, Filter, Eye, Edit, Trash2, Mail, Phone, Calendar, Grid, List } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";

// Import form component
import EmployeeForm from "../components/employees/EmployeeForm";
import BulkImport from "../components/employees/BulkImport";
import DeleteReasonModal from "../components/employees/DeleteReasonModal";

export default function Employees() {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const queryClient = useQueryClient();

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => backendApi.entities.Employee.list(),
    initialData: [],
  });

  const createEmployeeMutation = useMutation({
    mutationFn: (employeeData) => backendApi.entities.Employee.create(employeeData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setShowAddForm(false);
      setEditingEmployee(null);
      toast.success("Employee added successfully!");
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.Employee.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setShowAddForm(false);
      setEditingEmployee(null);
      toast.success("Employee updated successfully!");
    },
  });

  // Sends { leave_reason: { reason_code, reason_label, notes } } in the DELETE request body.
  const deleteEmployeeMutation = useMutation({
    mutationFn: ({ id, leave_reason }) =>
      backendApi.entities.Employee.delete(id, { leave_reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setDeletingEmployee(null);
      toast.success("Employee removed successfully!");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || "Failed to remove employee");
    },
  });

  const handleExportCSV = () => {
    if (employees.length === 0) {
      toast.error("No employees to export");
      return;
    }

    const headers = ["Employee ID", "First Name", "Last Name", "Email", "Phone", "Department", "Position", "Status", "Join Date", "Salary"];
    const csvData = employees.map(emp => [
      emp.employee_id || "",
      emp.first_name || "",
      emp.last_name || "",
      emp.email || "",
      emp.phone || "",
      emp.department || "",
      emp.position || "",
      emp.status || "",
      emp.join_date || "",
      emp.salary || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("CSV exported successfully!");
  };

  const handleSubmit = (employeeData) => {
    if (editingEmployee) {
      updateEmployeeMutation.mutate({ id: editingEmployee.id, data: employeeData });
    } else {
      createEmployeeMutation.mutate(employeeData);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setShowAddForm(true);
  };

  // Opens the reason modal instead of window.confirm
  const handleDelete = (employee) => {
    setDeletingEmployee(employee);
  };

  const confirmDelete = (leaveReasonPayload) => {
    if (!deletingEmployee) return;
    deleteEmployeeMutation.mutate({ id: deletingEmployee.id, leave_reason: leaveReasonPayload });
  };

  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(e => e.status === "Active").length;
  const onLeave = employees.filter(e => e.status === "On Leave").length;
  const newHires = employees.filter(e => {
    if (!e.join_date) return false;
    const joinDate = new Date(e.join_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return joinDate >= thirtyDaysAgo;
  }).length;

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || emp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const deptCounts = employees.reduce((acc, emp) => {
    acc[emp.department] = (acc[emp.department] || 0) + 1;
    return acc;
  }, {});

  const deptData = Object.keys(deptCounts).map(dept => ({
    name: dept,
    value: deptCounts[dept]
  }));

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <div className="p-4 md:p-8 bg-[#F9FAFB] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Employees</h1>
            <p className="text-gray-600">Manage your workforce</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowBulkImport(true)}
            >
              <Upload className="w-4 h-4" />
              Bulk Import
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleExportCSV}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              onClick={() => {
                setEditingEmployee(null);
                setShowAddForm(true);
              }}
            >
              <UserPlus className="w-4 h-4" />
              Add Employee
            </Button>
          </div>
        </motion.div>

        {/* Add/Edit Employee Form */}
        <AnimatePresence>
          {showAddForm && (
            <EmployeeForm
              employee={editingEmployee}
              managers={employees}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowAddForm(false);
                setEditingEmployee(null);
              }}
              isLoading={createEmployeeMutation.isLoading || updateEmployeeMutation.isLoading}
            />
          )}
        </AnimatePresence>

        {/* Bulk Import Modal */}
        <AnimatePresence>
          {showBulkImport && (
            <BulkImport
              onClose={() => setShowBulkImport(false)}
              onSuccess={() => {
                setShowBulkImport(false);
                queryClient.invalidateQueries({ queryKey: ['employees'] });
              }}
            />
          )}
        </AnimatePresence>

        {/* Delete Reason Modal */}
        <AnimatePresence>
          {deletingEmployee && (
            <DeleteReasonModal
              employee={deletingEmployee}
              isLoading={deleteEmployeeMutation.isLoading}
              onCancel={() => setDeletingEmployee(null)}
              onConfirm={confirmDelete}
            />
          )}
        </AnimatePresence>

        {/* Stats Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {[
            { value: totalEmployees, label: "Total Employees", change: employees.length > 0 ? "+0%" : "0%", color: "from-blue-500 to-blue-600", badge: employees.length > 0 ? "+0%" : "0%", icon: <Users className="w-8 h-8 opacity-80" /> },
            { value: activeEmployees, label: "Active", change: "+0%", color: "from-green-500 to-green-600", badge: "+0%", icon: <Users className="w-8 h-8 opacity-80" /> },
            { value: onLeave, label: "On Leave", change: "0", color: "from-orange-500 to-orange-600", badge: "0", icon: <Calendar className="w-8 h-8 opacity-80" /> },
            { value: newHires, label: "New Hires", change: "+0", color: "from-purple-500 to-purple-600", badge: "+0", icon: <UserPlus className="w-8 h-8 opacity-80" /> }
          ].map((stat, idx) => (
            <motion.div key={idx} variants={cardVariants}>
              <Card className={`border-none shadow-lg bg-gradient-to-br ${stat.color} text-white hover:shadow-xl transition-all`}>
                <CardContent className="p-6">
                  <motion.div
                    className="flex items-center justify-between mb-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {stat.icon}
                    <Badge className="bg-white/20 text-white">{stat.badge}</Badge>
                  </motion.div>
                  <motion.p
                    className="text-3xl font-bold mb-1"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                  >
                    {stat.value}
                  </motion.p>
                  <p className="text-sm opacity-90">{stat.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Search and View Toggle */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-12"
            />
          </div>
          <div className="flex gap-2">
            <div className="relative flex items-center h-12 px-3 border border-gray-200 rounded-md bg-white gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-full bg-transparent text-sm text-gray-700 focus:outline-none pr-2"
              >
                <option value="All">All statuses</option>
                <option value="Active">Active</option>
                <option value="On Leave">On Leave</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="icon"
              className="h-12 w-12"
              onClick={() => setViewMode("grid")}
            >
              <Grid className="w-5 h-5" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="icon"
              className="h-12 w-12"
              onClick={() => setViewMode("list")}
            >
              <List className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Employee Grid */}
        {filteredEmployees.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
          >
            {filteredEmployees.map((employee) => (
              <motion.div
                key={employee.id}
                variants={cardVariants}
                whileHover={{ scale: 1.03, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="border-none shadow-lg hover:shadow-2xl transition-all bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xl">
                        {employee.first_name?.[0]}{employee.last_name?.[0]}
                      </div>
                      <Badge className={
                        employee.status === "Active" ? "bg-green-100 text-green-800" :
                        employee.status === "On Leave" ? "bg-orange-100 text-orange-800" :
                        "bg-gray-100 text-gray-800"
                      }>
                        {employee.status}
                      </Badge>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {employee.first_name} {employee.last_name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">{employee.position}</p>
                    <p className="text-xs text-gray-500 mb-4">{employee.department}</p>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{employee.email}</span>
                      </div>
                      {employee.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{employee.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 gap-2"
                        onClick={() => handleEdit(employee)}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={() => handleEdit(employee)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 text-red-600"
                        onClick={() => handleDelete(employee)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Employees Yet</h3>
              <p className="text-gray-600 mb-6">Start by adding your first employee or import from CSV</p>
              <div className="flex gap-3 justify-center">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowAddForm(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add First Employee
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowBulkImport(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import from CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics */}
        {employees.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={deptData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={deptData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label
                    >
                      {deptData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}